import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { blogApi } from '../../api/blog'
import type { BlogPostDetail } from '../../types/blog'
import BlogMarkdownContent from '../../components/BlogMarkdownContent'
import BlogShell from './BlogShell'
import BlogShareBar from './BlogShareBar'
import {
  applyLandingSeo,
  getSiteUrl,
  initLandingAnalytics,
  useLandingDarkThemeEffect,
} from './landingUtils'
import { getBlogDefaultOgImageUrl } from '../../utils/blogShare'

function formatBlogDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<BlogPostDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    blogApi.getPost(slug)
      .then((res) => {
        if (!cancelled) {
          setPost(res.data)
          setNotFound(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPost(null)
          setNotFound(err?.response?.status === 404)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [slug])

  useEffect(() => {
    if (!post) return
    initLandingAnalytics()
    applyLandingSeo({
      page: 'blog',
      title: `${post.title} — KartinAI`,
      description: post.excerpt || post.title,
      path: `/blog/${post.slug}`,
      ogImage: post.cover_image_url || getBlogDefaultOgImageUrl(),
      ogType: 'article',
    })
  }, [post])

  useEffect(useLandingDarkThemeEffect(), [])

  if (!slug) {
    return <Navigate to="/blog" replace />
  }

  if (loading) {
    return (
      <BlogShell>
        <div className="blog-container">
          <p className="blog-empty">Загрузка…</p>
        </div>
      </BlogShell>
    )
  }

  if (notFound || !post) {
    return <Navigate to="/blog" replace />
  }

  return (
    <BlogShell>
      <article className="blog-container">
        <Link to="/blog" className="blog-back">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
          Все новости
        </Link>

        <header className="blog-article-header">
          <div className="blog-card-meta">
            <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
            {post.tags.map((tag) => (
              <span key={tag} className="blog-tag">{tag}</span>
            ))}
          </div>
          <h1 className="blog-article-title">{post.title}</h1>
          {post.cover_image_url ? (
            <figure className="blog-article-cover">
              <img
                src={post.cover_image_url}
                alt={post.cover_image_alt || post.title}
                className="blog-article-cover-img"
              />
            </figure>
          ) : null}
          <BlogShareBar
            title={post.title}
            excerpt={post.excerpt}
            slug={post.slug}
            coverImageUrl={post.cover_image_url}
            placement="header"
          />
        </header>

        <div className="blog-article-body">
          <BlogMarkdownContent content={post.body} />
        </div>

        <BlogShareBar
          title={post.title}
          excerpt={post.excerpt}
          slug={post.slug}
          coverImageUrl={post.cover_image_url}
          placement="footer"
        />

        <p className="blog-card-meta" style={{ marginTop: 48 }}>
          <a href={getSiteUrl('/register')}>Зарегистрироваться в KartinAI</a>
        </p>
      </article>
    </BlogShell>
  )
}
